import { Cond } from "functype";
/**
 * Shared validator functions using functional patterns
 * These are reused across all services for DRY principles
 */
export const validators = {
    // Basic validators
    url: (v) => /^https?:\/\/.+/.test(v),
    email: (v) => /^.+@.+\..+$/.test(v),
    jwt: (v) => v.startsWith("eyJ") && v.split(".").length === 3,
    hex: (minLen = 32) => (v) => /^[0-9a-f]+$/i.test(v) && v.length >= minLen,
    base64: (v) => /^[A-Za-z0-9+/]+=*$/.test(v) && v.length >= 32,
    int: (v) => /^\d+$/.test(v),
    positiveInt: (v) => validators.int(v) && parseInt(v) > 0,
    // Domain-specific validators (DRY for business logic)
    supabaseUrl: (v) => validators.url(v) && v.includes("supabase"),
    cloudflareUrl: (v) => validators.url(v) && v.includes("workers.dev"),
    stripeKey: (v) => v.startsWith("sk_") || v.startsWith("pk_"),
    // Composite validators
    enum: (values) => (v) => values.includes(v),
    minLength: (min) => (v) => v.length >= min,
    maxLength: (max) => (v) => v.length <= max,
    range: (min, max) => (v) => {
        const num = parseInt(v);
        return !isNaN(num) && num >= min && num <= max;
    },
    // Chainable validator builder
    chain: (...validators) => (v) => validators.every((validator) => validator(v)),
};
/**
 * Functional validation error message generator using Cond
 * Provides contextual error messages based on validation type
 */
export const getValidationError = (input) => Cond.of()
    .when(input.validator === validators.jwt && !validators.jwt(input.value), "Must be a valid JWT token starting with 'eyJ'")
    .when(input.validator === validators.supabaseUrl && !validators.supabaseUrl(input.value), "Must be a valid Supabase URL (https://your-project.supabase.co)")
    .when(input.validator === validators.email && !validators.email(input.value), "Must be a valid email address")
    .when(input.validator === validators.url && !validators.url(input.value), "Must be a valid URL starting with http:// or https://")
    .when(input.validator === validators.stripeKey && !validators.stripeKey(input.value), "Must be a valid Stripe key (sk_* or pk_*)")
    .when(input.validator === validators.positiveInt && !validators.positiveInt(input.value), "Must be a positive integer")
    .else("Invalid value");
/**
 * Functional validator for hex keys with length requirement
 */
export const createHexValidator = (minLength) => ({
    validator: validators.hex(minLength),
    error: `Must be a hex string of at least ${minLength} characters`,
});
/**
 * Functional validator for enum values
 */
export const createEnumValidator = (values) => ({
    validator: validators.enum(values),
    error: `Must be one of: ${values.join(", ")}`,
});
//# sourceMappingURL=validators.js.map